/**
 * Render Complete Detector - Advanced Resource Loading Priority Manager
 * Ensures third-party scripts load ONLY after 100% render completion
 * MOBILE OPTIMIZED: Reduces monitoring overhead to prevent visual glitching
 */

interface RenderCompleteConfig {
  timeout: number;
  checkInterval: number;
  enableLogging: boolean;
  performanceThresholds: {
    fcp: number; // First Contentful Paint
    lcp: number; // Largest Contentful Paint
    cls: number; // Cumulative Layout Shift
  };
  // 🔧 NEW: Mobile optimization settings
  mobileOptimized: boolean;
  mobileFixedDelay: number; // Fixed delay for mobile instead of complex monitoring
}

interface RenderState {
  domReady: boolean;
  layoutStable: boolean;
  paintComplete: boolean;
  imagesLoaded: boolean;
  fontsLoaded: boolean;
  reactMounted: boolean;
  userInteracted: boolean;
  performanceGood: boolean;
}

// 🔧 NEW: Mobile detection utility
const isMobileDevice = (): boolean => {
  if (typeof window === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
         window.innerWidth <= 768 ||
         'ontouchstart' in window;
};

export class RenderCompleteDetector {
  private config: RenderCompleteConfig;
  private state: RenderState;
  private callbacks: (() => void)[] = [];
  private observers: IntersectionObserver[] = [];
  private timeouts: NodeJS.Timeout[] = [];
  private completed: boolean = false;
  private startTime: number;
  private isMobile: boolean; // 🔧 NEW: Mobile detection flag

  constructor(config?: Partial<RenderCompleteConfig>) {
    this.isMobile = isMobileDevice();
    
    this.config = {
      timeout: 10000, // Maximum wait time
      checkInterval: this.isMobile ? 500 : 100, // 🔧 Longer intervals on mobile
      enableLogging: process.env.NODE_ENV === 'development',
      performanceThresholds: {
        fcp: 2500, // 2.5s for First Contentful Paint
        lcp: 4000, // 4s for Largest Contentful Paint  
        cls: 0.1   // Max Cumulative Layout Shift
      },
      // 🔧 NEW: Mobile-specific settings
      mobileOptimized: true,
      mobileFixedDelay: 3000, // 3s fixed delay on mobile - prevents visual glitching
      ...config
    };

    this.state = {
      domReady: false,
      layoutStable: false,
      paintComplete: false,
      imagesLoaded: false,
      fontsLoaded: false,
      reactMounted: false,
      userInteracted: false,
      performanceGood: false
    };

    this.startTime = performance.now();
    this.init();
  }

  private init(): void {
    this.log(`🎯 [RenderDetector] Initializing ${this.isMobile ? 'MOBILE' : 'DESKTOP'} render detection...`);
    
    // 🔧 MOBILE OPTIMIZATION: Use simple fixed delay to prevent visual glitching
    if (this.isMobile && this.config.mobileOptimized) {
      this.log('📱 [RenderDetector] Using mobile-optimized simple detection');
      this.initMobileOptimizedDetection();
      return;
    }
    
    // Desktop or non-optimized mode: Use full monitoring
    this.log('💻 [RenderDetector] Using full desktop monitoring');
    this.initFullMonitoring();
  }

  // 🔧 NEW: Mobile-optimized detection with minimal monitoring
  private initMobileOptimizedDetection(): void {
    // Only check essential states - no layout monitoring
    this.checkDOMReady();
    this.monitorReactLifecycle();
    
    // Set all states to ready after fixed delay to prevent interference
    const mobileTimeout = setTimeout(() => {
      this.state.layoutStable = true;
      this.state.paintComplete = true;
      this.state.imagesLoaded = true;
      this.state.fontsLoaded = true;
      this.state.performanceGood = true;
      this.state.userInteracted = true;
      
      this.log('📱 [RenderDetector] Mobile fixed delay completed - all states ready');
      this.triggerCompletion();
    }, this.config.mobileFixedDelay);
    
    this.timeouts.push(mobileTimeout);
  }

  // 🔧 MODIFIED: Full monitoring for desktop only
  private initFullMonitoring(): void {
    // 1. DOM Readiness
    this.checkDOMReady();
    
    // 2. Performance monitoring
    this.monitorPerformance();
    
    // 3. Layout stability (DESKTOP ONLY - causes mobile glitching)
    this.monitorLayoutStability();
    
    // 4. Image loading
    this.monitorImageLoading();
    
    // 5. Font loading
    this.monitorFontLoading();
    
    // 6. React lifecycle
    this.monitorReactLifecycle();
    
    // 7. User interaction tracking
    this.trackUserInteraction();
    
    // 8. Continuous checking
    this.startContinuousCheck();
    
    // 9. Fallback timeout
    this.setFallbackTimeout();
  }

  private checkDOMReady(): void {
    const updateDOMState = () => {
      this.state.domReady = document.readyState === 'complete';
      this.log(`📄 [RenderDetector] DOM Ready: ${this.state.domReady}`);
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', updateDOMState);
      window.addEventListener('load', updateDOMState);
    } else {
      updateDOMState();
    }
  }

  private monitorPerformance(): void {
    // Use PerformanceObserver for real-time metrics
    if ('PerformanceObserver' in window) {
      try {
        // Monitor paint events
        const paintObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.entryType === 'paint') {
              if (entry.name === 'first-contentful-paint') {
                const fcp = entry.startTime;
                this.log(`🎨 [RenderDetector] First Contentful Paint: ${fcp.toFixed(2)}ms`);
                this.state.paintComplete = fcp < this.config.performanceThresholds.fcp;
              }
            }
          }
        });
        paintObserver.observe({ entryTypes: ['paint'] });

        // Monitor largest contentful paint
        const lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1];
          if (lastEntry) {
            const lcp = lastEntry.startTime;
            this.log(`🖼️ [RenderDetector] Largest Contentful Paint: ${lcp.toFixed(2)}ms`);
            this.state.performanceGood = lcp < this.config.performanceThresholds.lcp;
          }
        });
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });

      } catch (error) {
        this.log('⚠️ [RenderDetector] PerformanceObserver not supported, using fallback');
        this.useFallbackPerformanceCheck();
      }
    } else {
      this.useFallbackPerformanceCheck();
    }
  }

  private useFallbackPerformanceCheck(): void {
    // Fallback for browsers without PerformanceObserver
    setTimeout(() => {
      this.state.paintComplete = true;
      this.state.performanceGood = true;
      this.state.layoutStable = true;
      this.log('✅ [RenderDetector] Using fallback performance indicators');
    }, 2000);
  }

  private monitorLayoutStability(): void {
    let lastHeight = document.documentElement.scrollHeight;
    let stableCount = 0;
    const requiredStableCount = 5; // 5 consecutive stable checks

    const checkStability = () => {
      const currentHeight = document.documentElement.scrollHeight;
      if (Math.abs(currentHeight - lastHeight) < 5) { // 5px tolerance
        stableCount++;
        if (stableCount >= requiredStableCount) {
          this.state.layoutStable = true;
          this.log('📏 [RenderDetector] Layout is stable');
          return;
        }
      } else {
        stableCount = 0;
      }
      lastHeight = currentHeight;
      
      const timeout = setTimeout(checkStability, this.config.checkInterval);
      this.timeouts.push(timeout);
    };

    const initialTimeout = setTimeout(checkStability, 500);
    this.timeouts.push(initialTimeout);
  }

  private monitorImageLoading(): void {
    const images = document.querySelectorAll('img');
    let loadedCount = 0;
    const totalCount = images.length;

    if (totalCount === 0) {
      this.state.imagesLoaded = true;
      this.log('🖼️ [RenderDetector] No images to load');
      return;
    }

    const checkImageLoaded = () => {
      loadedCount++;
      this.log(`🖼️ [RenderDetector] Images loaded: ${loadedCount}/${totalCount}`);
      if (loadedCount >= totalCount) {
        this.state.imagesLoaded = true;
      }
    };

    images.forEach(img => {
      if (img.complete) {
        checkImageLoaded();
      } else {
        img.addEventListener('load', checkImageLoaded);
        img.addEventListener('error', checkImageLoaded); // Count errors as "loaded"
      }
    });

    // ✅ ANTI-BLOCKING: Shorter timeout to prevent external resources from blocking
    const fallbackTimeout = setTimeout(() => {
      if (!this.state.imagesLoaded) {
        this.state.imagesLoaded = true;
        this.log('⏰ [RenderDetector] Image loading timeout reached (3s), proceeding to prevent blocking...');
      }
    }, 3000); // Reduced from 5s to 3s to prevent external blocking
    this.timeouts.push(fallbackTimeout);
  }

  private monitorFontLoading(): void {
    if ('fonts' in document) {
      (document as any).fonts.ready.then(() => {
        this.state.fontsLoaded = true;
        this.log('🔤 [RenderDetector] All fonts loaded');
      }).catch(() => {
        this.state.fontsLoaded = true; // Proceed even if fonts fail
        this.log('⚠️ [RenderDetector] Font loading failed, proceeding...');
      });
    } else {
      // Fallback for browsers without FontFaceSet API
      const fallbackTimeout = setTimeout(() => {
        this.state.fontsLoaded = true;
        this.log('🔤 [RenderDetector] Using font loading fallback');
      }, 1000);
      this.timeouts.push(fallbackTimeout);
    }
  }

  private monitorReactLifecycle(): void {
    // Listen for React app readiness
    const checkReactMount = () => {
      const reactRoot = document.getElementById('root');
      if (reactRoot && reactRoot.children.length > 0) {
        this.state.reactMounted = true;
        this.log('⚛️ [RenderDetector] React app mounted');
        return;
      }
      const timeout = setTimeout(checkReactMount, this.config.checkInterval);
      this.timeouts.push(timeout);
    };

    checkReactMount();

    // Listen for custom React ready event
    window.addEventListener('react-app-ready', () => {
      this.state.reactMounted = true;
      this.log('⚛️ [RenderDetector] React app ready event received');
    });
  }

  private trackUserInteraction(): void {
    const events = ['scroll', 'click', 'touchstart', 'keydown'];
    const onInteraction = () => {
      this.state.userInteracted = true;
      this.log('👆 [RenderDetector] User interaction detected');
      events.forEach(event => {
        document.removeEventListener(event, onInteraction);
      });
    };

    events.forEach(event => {
      document.addEventListener(event, onInteraction, { passive: true, once: true });
    });
  }

  private startContinuousCheck(): void {
    const interval = setInterval(() => {
      if (this.isRenderComplete()) {
        clearInterval(interval);
        this.triggerCompletion();
      }
    }, this.config.checkInterval);

    this.timeouts.push(interval as any);
  }

  private setFallbackTimeout(): void {
    const timeout = setTimeout(() => {
      if (!this.completed) {
        this.log('⏰ [RenderDetector] Fallback timeout reached, forcing completion');
        this.triggerCompletion();
      }
    }, this.config.timeout);

    this.timeouts.push(timeout);
  }

  private isRenderComplete(): boolean {
    const { domReady, layoutStable, paintComplete, imagesLoaded, fontsLoaded, reactMounted } = this.state;
    
    // Core requirements for render completion
    const coreRequirements = domReady && layoutStable && paintComplete && reactMounted;
    
    // Additional requirements (with more lenient timing)
    const additionalRequirements = imagesLoaded && fontsLoaded;
    
    // At minimum, need core requirements + some time passed
    const minimumTime = performance.now() - this.startTime > 1000; // At least 1 second
    
    return coreRequirements && (additionalRequirements || minimumTime);
  }

  private triggerCompletion(): void {
    if (this.completed) return;
    
    this.completed = true;
    const totalTime = performance.now() - this.startTime;
    
    this.log('🎉 [RenderDetector] Render completion detected!');
    this.log('📊 [RenderDetector] Final state:', this.state);
    this.log(`⏱️ [RenderDetector] Total time: ${totalTime.toFixed(2)}ms`);
    
    // Cleanup
    this.cleanup();
    
    // Execute callbacks
    this.callbacks.forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.error('❌ [RenderDetector] Callback error:', error);
      }
    });
  }

  private cleanup(): void {
    // Clear timeouts
    this.timeouts.forEach(timeout => clearTimeout(timeout));
    this.timeouts = [];
    
    // Disconnect observers
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];
  }

  private log(...args: any[]): void {
    if (this.config.enableLogging) {
      console.log(...args);
    }
  }

  // Public API
  public onRenderComplete(callback: () => void): void {
    if (this.completed) {
      setTimeout(callback, 0); // Execute immediately if already complete
    } else {
      this.callbacks.push(callback);
    }
  }

  public isComplete(): boolean {
    return this.completed;
  }

  public getState(): RenderState {
    return { ...this.state };
  }

  public forceCompletion(): void {
    this.log('🔧 [RenderDetector] Forcing completion manually');
    this.triggerCompletion();
  }
}

// Global singleton instance
let detectorInstance: RenderCompleteDetector | null = null;

export const getRenderCompleteDetector = (): RenderCompleteDetector => {
  if (!detectorInstance) {
    detectorInstance = new RenderCompleteDetector();
  }
  return detectorInstance;
};

// Convenience function for common usage
export const onRenderComplete = (callback: () => void): void => {
  getRenderCompleteDetector().onRenderComplete(callback);
};

// Type exports
export type { RenderCompleteConfig, RenderState }; 